import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { geoEqualEarth, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import Flag from "react-world-flags";
import { Plus, Minus, RotateCcw } from "lucide-react";
import { COUNTRY_COORDS } from "@/lib/countryCoords";
import worldTopo from "@/lib/world-110m.json";

const WORLD_FEATURES = feature(worldTopo, worldTopo.objects.countries).features;
const W = 800;
const H = 440;
const MIN_K = 1;
const MAX_K = 8;

export default function HeroMap({ speeches = [] }) {
  const navigate = useNavigate();
  const svgRef = useRef(null);
  const geos = WORLD_FEATURES;
  const [hover, setHover] = useState(null);
  const [view, setView] = useState({ k: 1, x: 0, y: 0 });
  const drag = useRef({ active: false, moved: false, sx: 0, sy: 0, ox: 0, oy: 0 });

  const projection = useMemo(() => {
    const proj = geoEqualEarth();
    proj.fitExtent([[14, 14], [W - 14, H - 14]], { type: "FeatureCollection", features: geos });
    return proj;
  }, [geos]);

  const pathGen = useMemo(() => geoPath(projection), [projection]);

  const markers = useMemo(() => {
    const byCode = {};
    for (const s of speeches) {
      const code = (s.country_code || "").toUpperCase();
      if (!code || !COUNTRY_COORDS[code]) continue;
      if (!byCode[code]) byCode[code] = { code, country: s.country, leaders: new Set(), count: 0 };
      byCode[code].leaders.add(s.leader);
      byCode[code].count += 1;
    }
    return Object.values(byCode).map((m) => {
      const [x, y] = projection(COUNTRY_COORDS[m.code]) || [0, 0];
      return { ...m, x, y, leaders: [...m.leaders] };
    });
  }, [speeches, projection]);

  const toSvg = (clientX, clientY) => {
    const rect = svgRef.current.getBoundingClientRect();
    return [((clientX - rect.left) / rect.width) * W, ((clientY - rect.top) / rect.height) * H];
  };

  const clamp = (k) => Math.max(MIN_K, Math.min(MAX_K, k));

  const zoomAt = (mx, my, factor) => {
    setView((v) => {
      const k = clamp(v.k * factor);
      const scale = k / v.k;
      let x = mx - (mx - v.x) * scale;
      let y = my - (my - v.y) * scale;
      // clamp translation so map stays in view
      const maxX = 0, minX = W - W * k;
      const maxY = 0, minY = H - H * k;
      x = Math.max(minX, Math.min(maxX, x));
      y = Math.max(minY, Math.min(maxY, y));
      return { k, x, y };
    });
  };

  const onWheel = (e) => {
    e.preventDefault();
    const [mx, my] = toSvg(e.clientX, e.clientY);
    zoomAt(mx, my, e.deltaY < 0 ? 1.2 : 1 / 1.2);
  };

  const onPointerDown = (e) => {
    drag.current = { active: true, moved: false, sx: e.clientX, sy: e.clientY, ox: view.x, oy: view.y };
  };
  const onPointerMove = (e) => {
    if (!drag.current.active) return;
    const rect = svgRef.current.getBoundingClientRect();
    const dx = ((e.clientX - drag.current.sx) / rect.width) * W;
    const dy = ((e.clientY - drag.current.sy) / rect.height) * H;
    if (Math.abs(e.clientX - drag.current.sx) + Math.abs(e.clientY - drag.current.sy) > 4) drag.current.moved = true;
    setView((v) => {
      const minX = W - W * v.k, minY = H - H * v.k;
      const x = Math.max(minX, Math.min(0, drag.current.ox + dx));
      const y = Math.max(minY, Math.min(0, drag.current.oy + dy));
      return { ...v, x, y };
    });
  };
  const endDrag = () => { drag.current.active = false; };

  const btnZoom = (factor) => zoomAt(W / 2, H / 2, factor);
  const reset = () => setView({ k: 1, x: 0, y: 0 });

  return (
    <div className="relative w-full" data-testid="hero-map">
      <div className="rounded-lg overflow-hidden border border-white/12 bg-[#0b1226]/85 backdrop-blur-md shadow-[0_20px_50px_-20px_rgba(0,0,0,0.7)]">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          className="w-full h-auto block select-none"
          style={{ cursor: drag.current.active ? "grabbing" : "grab", touchAction: "none" }}
          onWheel={onWheel}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerLeave={endDrag}
        >
          <g transform={`translate(${view.x}, ${view.y}) scale(${view.k})`}>
            {geos.map((geo, i) => (
              <path key={geo.id ?? i} d={pathGen(geo)} fill="#3a4f8f" stroke="#7189c9" strokeWidth={0.6 / view.k} />
            ))}
            {markers.map((m) => {
              const href = `/archivio?country=${encodeURIComponent(m.country)}`;
              const label = `Mostra i discorsi di ${m.leaders.map((l) => l.trim()).join(", ")} – ${m.country}`;
              const go = () => { if (!drag.current.moved) navigate(href); };
              return (
                <g key={m.code}>
                  <g transform={`translate(${m.x}, ${m.y}) scale(${1 / view.k})`} style={{ pointerEvents: "none" }}>
                    <circle r={14} fill="#4f6bff" opacity={0.18} className="hero-marker-pulse" />
                  </g>
                  <a
                    href={href}
                    className="hero-map-link"
                    tabIndex={0}
                    role="link"
                    data-testid={`hero-marker-${m.code}`}
                    aria-label={label}
                    onClick={(e) => { e.preventDefault(); go(); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); navigate(href); } }}
                    onFocus={() => setHover(m)}
                    onBlur={() => setHover(null)}
                    onMouseEnter={() => setHover(m)}
                    onMouseLeave={() => setHover(null)}
                    style={{ cursor: "pointer", outline: "none" }}
                  >
                    <g transform={`translate(${m.x}, ${m.y}) scale(${1 / view.k})`}>
                      <circle r={16} fill="transparent" style={{ pointerEvents: "all" }} />
                      <circle className="marker-dot" r={hover?.code === m.code ? 7 : 5} fill="#4f6bff" stroke="#fff" strokeWidth={1.5} style={{ transition: "r 0.15s", pointerEvents: "none" }} />
                    </g>
                  </a>
                </g>
              );
            })}
          </g>
        </svg>
      </div>

      {/* Intestazione del pannello */}
      <div className="absolute top-3 left-3 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-white/90 bg-black/70 backdrop-blur px-2.5 py-1 rounded-sm">
        <span className="h-2 w-2 rounded-full bg-[#4f6bff]" /> Discorsi nel mondo
      </div>

      {/* Zoom controls */}
      <div className="absolute top-3 right-3 flex flex-col gap-1.5">
        <button onClick={() => btnZoom(1.5)} data-testid="map-zoom-in" aria-label="Ingrandisci" className="h-8 w-8 flex items-center justify-center rounded-sm bg-black/50 backdrop-blur border border-white/15 text-white hover:bg-[#4f6bff] transition-colors">
          <Plus className="h-4 w-4" />
        </button>
        <button onClick={() => btnZoom(1 / 1.5)} data-testid="map-zoom-out" aria-label="Riduci" className="h-8 w-8 flex items-center justify-center rounded-sm bg-black/50 backdrop-blur border border-white/15 text-white hover:bg-[#4f6bff] transition-colors">
          <Minus className="h-4 w-4" />
        </button>
        <button onClick={reset} data-testid="map-reset" aria-label="Reimposta" className="h-8 w-8 flex items-center justify-center rounded-sm bg-black/50 backdrop-blur border border-white/15 text-white hover:bg-[#4f6bff] transition-colors">
          <RotateCcw className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Hover info */}
      <div className="absolute bottom-3 left-3 right-3 min-h-[52px]" data-testid="hero-map-info">
        {hover ? (
          <div
            className="flex items-center gap-3 bg-black/70 backdrop-blur border border-white/10 rounded-sm px-3 py-2 cursor-pointer animate-[fadeUp_0.25s_ease-out]"
            onClick={() => navigate(`/archivio?country=${encodeURIComponent(hover.country)}`)}
          >
            {hover.code && (
              <span className="w-8 h-5 overflow-hidden rounded-[2px] border border-white/20 shrink-0">
                <Flag code={hover.code} className="w-full h-full object-cover" fallback={<span />} />
              </span>
            )}
            <div className="min-w-0">
              <div className="text-sm font-semibold text-white truncate">{hover.country}</div>
              <div className="text-[11px] text-white/75 truncate">{hover.leaders.map((l) => l.trim()).join(", ")} · {hover.count} discors{hover.count === 1 ? "o" : "i"}</div>
            </div>
            <span className="ml-auto text-[11px] text-[#8ba0ff] shrink-0">Vedi →</span>
          </div>
        ) : (
          <div className="text-xs text-white/80 px-1 font-medium">
            Seleziona un Paese per vedere i discorsi disponibili.
          </div>
        )}
      </div>
    </div>
  );
}
